/**
 * Unified Audio Manager
 *
 * Single API for managing all audio in the app:
 * - BGM (Background Music) - Web Audio or HTML5
 * - Video audio routing
 * - AudioContext management
 * - Volume ducking, muting, fading
 *
 * Automatically handles:
 * - iOS Safari compatibility
 * - System detection (Web Audio vs HTML5)
 * - Preventing conflicts between systems
 * - AudioContext unlocking
 *
 * NOTE: This module syncs with useAudioStore for centralized state management
 */

// ============================================
// Internal Imports (from existing systems)
// ============================================
import {
	getAudioContext as getWebAudioContext,
	pauseBgm as pauseWebBgm,
	resumeBgm as resumeWebBgm,
	getBgmAudio as getWebBgmAudio,
	setBgmVolume as setWebBgmVolume,
	setBgmMuted as setWebBgmMuted,
	toggleBgmMuted as toggleWebBgmMuted,
	isBgmPlaying as isWebBgmPlaying,
	isBgmMuted as isWebBgmMuted,
	fadeBgmTo as fadeWebBgmTo,
	getBgmPosition as getWebBgmPosition,
	setBgmPosition as setWebBgmPosition,
	ensureBgmStarted as ensureWebBgmStarted,
	getCurrentBgmUrl as getWebBgmUrl,
	DEFAULT_BGM_URL as WEB_DEFAULT_BGM_URL,
	QUEST_BGM_URL as WEB_QUEST_BGM_URL,
	cleanupBgm as cleanupWebBgm,
} from "./audiohelper/bgmPlayer";

import {
	getBgmElement as getHtmlBgmElement,
	startBgm as startHtmlBgm,
	pauseBgm as pauseHtmlBgm,
	resumeBgm as resumeHtmlBgm,
	setBgmVolume as setHtmlBgmVolume,
	setBgmMuted as setHtmlBgmMuted,
	toggleBgmMuted as toggleHtmlBgmMuted,
	isBgmPlaying as isHtmlBgmPlaying,
	isBgmMuted as isHtmlBgmMuted,
	fadeBgmTo as fadeHtmlBgmTo,
	getBgmCurrentTime as getHtmlBgmPosition,
	setBgmCurrentTime as setHtmlBgmPosition,
	getBgmInfo as getHtmlBgmInfo,
	cleanupSimpleBgm as cleanupHtmlBgm,
} from "./audiohelper/simpleBgmPlayer";

// ============================================
// Constants
// ============================================
export const DEFAULT_BGM_URL = WEB_DEFAULT_BGM_URL;
export const QUEST_BGM_URL = WEB_QUEST_BGM_URL;

// ============================================
// Internal State
// ============================================
let activeBgmSystem = null; // 'web', 'html5', or null
let videoSource = null;
let videoGain = null;
let previousBgmVolume = null; // For ducking
let isUnmuting = false; // Flag to prevent restoration during unmute

// ============================================
// System Detection & Selection
// ============================================

/**
 * Detect which BGM system is currently active
 * @returns {'web' | 'html5' | null}
 */
function detectActiveBgmSystem() {
	const webBgm = getWebBgmAudio();
	const htmlBgm = getHtmlBgmElement();

	if (webBgm && isWebBgmPlaying()) {
		return "web";
	}
	if (htmlBgm && isHtmlBgmPlaying()) {
		return "html5";
	}
	return null;
}

/**
 * Get the currently active BGM system
 * @returns {'web' | 'html5' | null}
 */
export function getActiveBgmSystem() {
	if (activeBgmSystem) {
		return activeBgmSystem;
	}
	// Auto-detect if not explicitly set
	activeBgmSystem = detectActiveBgmSystem();
	return activeBgmSystem;
}

/**
 * Helper to execute function based on active BGM system
 * @param {Function} webFn - Function to call for Web Audio system
 * @param {Function} html5Fn - Function to call for HTML5 system
 * @param {*} defaultValue - Default return value if no system active
 * @returns {*} Result of the appropriate function
 */
function withBgmSystem(webFn, html5Fn, defaultValue = null) {
	const system = getActiveBgmSystem();
	if (system === "web") {
		return webFn();
	} else if (system === "html5") {
		return html5Fn();
	}
	return defaultValue;
}

/**
 * Set which BGM system to use (or 'auto' for auto-detection)
 * @param {'web' | 'html5' | 'auto'} system
 */
export function setBgmSystem(system) {
	if (system === "auto") {
		activeBgmSystem = null; // Will auto-detect
	} else {
		activeBgmSystem = system;
	}
}

// ============================================
// AudioContext Management
// ============================================

/**
 * Get the Web Audio API AudioContext
 * @returns {AudioContext}
 */
export function getAudioContext() {
	return getWebAudioContext();
}

/**
 * Unlock AudioContext (required for iOS Safari)
 * @returns {Promise<boolean>} - true if unlocked
 */
export async function unlockAudioContext() {
	const ctx = getAudioContext();
	if (ctx.state === "suspended") {
		try {
			await ctx.resume();
			return ctx.state === "running";
		} catch (error) {
			console.warn("Failed to unlock AudioContext:", error);
			return false;
		}
	}
	return ctx.state === "running";
}

/**
 * Resume AudioContext if suspended
 * @returns {Promise<boolean>}
 */
export async function resumeAudioContext() {
	return unlockAudioContext();
}

// ============================================
// Video Audio Routing
// ============================================

/**
 * Route video audio through Web Audio API (for mixing with BGM)
 * @param {HTMLVideoElement} videoElement
 */
export function routeVideoAudio(videoElement) {
	if (!videoElement) {
		return;
	}

	const ctx = getAudioContext();

	// Only create once
	if (!videoSource) {
		try {
			videoSource = ctx.createMediaElementSource(videoElement);
			videoGain = ctx.createGain();
			videoGain.gain.value = 1.0;

			videoSource.connect(videoGain);
			videoGain.connect(ctx.destination);

			console.log("✅ Video audio routed through Web Audio API");
		} catch (error) {
			console.warn("Failed to route video audio:", error);
		}
	}
}

/**
 * Remove video audio routing
 * @param {HTMLVideoElement} videoElement
 */
export function unrouteVideoAudio(_videoElement) {
	if (videoSource) {
		try {
			videoSource.disconnect();
			videoGain?.disconnect();
			videoSource = null;
			videoGain = null;
			console.log("✅ Video audio routing removed");
		} catch (error) {
			console.warn("Failed to unroute video audio:", error);
		}
	}
}

/**
 * Set video audio volume
 * @param {HTMLVideoElement} videoElement
 * @param {number} volume - 0.0 to 1.0
 */
export function setVideoAudioVolume(videoElement, volume) {
	if (videoGain) {
		videoGain.gain.value = Math.max(0, Math.min(1, volume));
	} else if (videoElement) {
		videoElement.volume = Math.max(0, Math.min(1, volume));
	}
}

// ============================================
// Unified BGM API
// ============================================

/**
 * Start BGM with automatic system selection
 * @param {Object} options
 * @param {string} options.url - BGM URL
 * @param {number} options.volume - Volume (0.0 to 1.0)
 * @param {boolean} options.muted - Muted state
 * @param {number} options.startPosition - Start position in seconds
 * @param {'web' | 'html5' | 'auto'} options.system - Which system to use
 * @returns {Promise<boolean>}
 */
export async function startBgm({
	url = DEFAULT_BGM_URL,
	volume = 0.2, // Default BGM volume (0.0 to 1.0)
	muted = false,
	startPosition = null,
	system = "auto",
} = {}) {
	// Determine which system to use
	const targetSystem =
		system === "auto" ? getActiveBgmSystem() || "web" : system;

	// Stop the other system if needed
	if (targetSystem === "web" && activeBgmSystem === "html5") {
		pauseHtmlBgm();
	} else if (targetSystem === "html5" && activeBgmSystem === "web") {
		pauseWebBgm();
	}

	// Start the selected system
	try {
		if (targetSystem === "web") {
			await ensureWebBgmStarted({ url, volume, muted, startPosition });
			activeBgmSystem = "web";
		} else {
			await startHtmlBgm(url, volume, muted);
			if (startPosition !== null) {
				await setHtmlBgmPosition(startPosition);
			}
			activeBgmSystem = "html5";
		}
		return true;
	} catch (error) {
		console.error("Failed to start BGM:", error);
		activeBgmSystem = null;
		return false;
	}
}

/**
 * Pause BGM
 */
export function pauseBgm() {
	const system = getActiveBgmSystem();
	if (system === "web") {
		pauseWebBgm();
	} else if (system === "html5") {
		pauseHtmlBgm();
	}
	activeBgmSystem = null;
}

/**
 * Resume BGM
 * @returns {Promise<boolean>}
 */
export async function resumeBgm() {
	const system = getActiveBgmSystem();
	if (system === "web") {
		return await resumeWebBgm();
	} else if (system === "html5") {
		return await resumeHtmlBgm();
	}
	return false;
}

/**
 * Check if BGM is playing
 * @returns {boolean}
 */
export function isBgmPlaying() {
	const system = getActiveBgmSystem();
	if (system === "web") {
		return isWebBgmPlaying();
	} else if (system === "html5") {
		return isHtmlBgmPlaying();
	}
	return false;
}

/**
 * Check if BGM is muted
 * @returns {boolean}
 */
export function isBgmMuted() {
	const system = getActiveBgmSystem();
	if (system === "web") {
		return isWebBgmMuted();
	}
	if (system === "html5") {
		return isHtmlBgmMuted();
	}
	return false;
}

/**
 * Set BGM volume
 * @param {number} volume - 0.0 to 1.0
 */
export function setBgmVolume(volume) {
	// CRITICAL: Don't change volume if BGM is muted
	// This prevents volume from being set while muted (which can cause issues)
	if (isBgmMuted()) {
		console.log(
			"🔊 setBgmVolume: BGM is muted - skipping volume change to preserve muted state"
		);
		return;
	}

	withBgmSystem(
		() => setWebBgmVolume(volume),
		() => setHtmlBgmVolume(volume)
	);

	// Sync with audioStore if available
	const audioStore = getAudioStore();
	if (audioStore) {
		audioStore.setBgmVolume(volume);
	}
}

/**
 * Get BGM volume
 * @returns {number}
 */
export function getBgmVolume() {
	return withBgmSystem(
		() => {
			const audio = getWebBgmAudio();
			return audio?.volume || 0;
		},
		() => {
			const audio = getHtmlBgmElement();
			return audio?.volume || 0;
		},
		0
	);
}

/**
 * Fade BGM volume
 * @param {number} targetVolume - Target volume (0.0 to 1.0)
 * @param {number} duration - Duration in seconds
 */
export function fadeBgmVolume(targetVolume, duration = 0.5) {
	// CRITICAL: Don't fade if BGM is muted
	// This prevents volume from being changed while muted
	if (isBgmMuted()) {
		console.log(
			"🔊 fadeBgmVolume: BGM is muted - skipping fade to preserve muted state"
		);
		return;
	}

	withBgmSystem(
		() => fadeWebBgmTo(targetVolume, duration),
		() => fadeHtmlBgmTo(targetVolume, duration * 1000) // HTML5 uses milliseconds
	);
}

/**
 * Set BGM muted state
 * @param {boolean} muted
 */
export function setBgmMuted(muted) {
	withBgmSystem(
		() => setWebBgmMuted(muted),
		() => setHtmlBgmMuted(muted)
	);
}

/**
 * Toggle BGM muted state
 * @returns {boolean} - New muted state
 */
export function toggleBgmMuted() {
	return withBgmSystem(
		() => toggleWebBgmMuted(),
		() => toggleHtmlBgmMuted(),
		false
	);
}

/**
 * Get BGM position (current playback time)
 * @returns {number} - Position in seconds
 */
export function getBgmPosition() {
	return withBgmSystem(
		() => getWebBgmPosition(),
		() => getHtmlBgmPosition(),
		0
	);
}

/**
 * Set BGM position
 * @param {number} position - Position in seconds
 */
export async function setBgmPosition(position) {
	const system = getActiveBgmSystem();
	if (system === "web") {
		setWebBgmPosition(position);
	} else if (system === "html5") {
		await setHtmlBgmPosition(position);
	}
}

// ============================================
// Helper Functions
// ============================================

/**
 * Resume HTML5 BGM if it exists (helper to reduce duplication)
 * @returns {Promise<void>}
 */
async function resumeHtmlBgmIfExists() {
	const htmlBgm = getHtmlBgmElement();
	if (htmlBgm) {
		htmlBgm.muted = false;
		htmlBgm.volume = previousBgmVolume || 0.2;
		previousBgmVolume = null;

		if (htmlBgm.paused) {
			try {
				await htmlBgm.play();
			} catch (err) {
				console.warn("Failed to resume HTML5 BGM:", err);
			}
		}
	}
}

/**
 * Check if any video element is currently playing
 * @returns {boolean}
 */
function hasPlayingVideo() {
	const videoElements = document.querySelectorAll("video");
	return Array.from(videoElements).some((v) => !v.paused && !v.ended);
}

/**
 * Get audioStore safely
 * @returns {Object|null}
 */
function getAudioStore() {
	return typeof window !== "undefined" && window.__audioStore
		? window.__audioStore
		: null;
}

// ============================================
// BGM Ducking (for video playback)
// ============================================

/**
 * Duck BGM volume (lower it, e.g., when video plays)
 * @param {number} duckVolume - Volume to duck to (default: 0.03)
 * @param {boolean} savePrevious - Save current volume for restoration (default: true)
 */
export function duckBgm(duckVolume = 0.03, savePrevious = true) {
	const system = getActiveBgmSystem();
	if (!system) {
		return;
	}

	if (savePrevious && previousBgmVolume === null) {
		previousBgmVolume = getBgmVolume();
	}

	setBgmVolume(duckVolume);
}

/**
 * Restore BGM volume after ducking
 * @param {number} targetVolume - Volume to restore to (if not saved, uses this)
 */
export function restoreBgmVolume(targetVolume = null) {
	const system = getActiveBgmSystem();
	if (!system) {
		return;
	}

	const restoreVolume =
		targetVolume !== null ? targetVolume : previousBgmVolume || 0.5;
	setBgmVolume(restoreVolume);
	previousBgmVolume = null;
}

/**
 * Duck BGM with fade
 * @param {number} duckVolume - Volume to duck to
 * @param {number} duration - Fade duration in seconds
 * @param {boolean} savePrevious - Save current volume
 */
export function duckBgmWithFade(
	duckVolume = 0.03,
	duration = 0.5,
	savePrevious = true
) {
	const system = getActiveBgmSystem();
	if (!system) {
		return;
	}

	// CRITICAL: Don't duck if BGM is muted
	// This prevents volume from being changed while muted
	if (isBgmMuted()) {
		console.log(
			"🔊 duckBgmWithFade: BGM is muted - skipping duck to preserve muted state"
		);
		return;
	}

	if (savePrevious && previousBgmVolume === null) {
		previousBgmVolume = getBgmVolume();
	}

	// Sync with audioStore if available
	const audioStore = getAudioStore();
	if (audioStore) {
		audioStore.duckBgm(duckVolume);
		audioStore.setBgmVolume(duckVolume);
	}

	fadeBgmVolume(duckVolume, duration);
}

/**
 * Restore BGM volume with fade
 * @param {number} targetVolume - Volume to restore to
 * @param {number} duration - Fade duration in seconds
 */
export function restoreBgmVolumeWithFade(targetVolume = null, duration = 0.5) {
	const system = getActiveBgmSystem();
	if (!system) {
		return;
	}

	// CRITICAL: Don't restore if BGM is muted
	// This prevents volume from being changed while muted
	if (isBgmMuted()) {
		console.log(
			"🔊 restoreBgmVolumeWithFade: BGM is muted - skipping restore to preserve muted state"
		);
		return;
	}

	// CRITICAL: Don't restore if we're in the middle of unmuting
	// This prevents race conditions where cleanup tries to restore while unmute is setting volume
	if (isUnmuting) {
		console.log(
			"🔊 restoreBgmVolumeWithFade: Unmute in progress - NOT restoring"
		);
		return;
	}

	// CRITICAL: Check if there's a video element playing (more reliable than store)
	// This prevents restoration when video is actually playing, even if store state is wrong
	const hasPlayingVideoCheck = hasPlayingVideo();

	// CRITICAL: Check audioStore first - if video is playing and BGM is ducked, don't restore!
	// Only restore if video is NOT playing or BGM is NOT ducked
	const audioStore = getAudioStore();
	if (audioStore) {
		const { isVideoPlaying, isBgmDucked, duckVolume } = audioStore;
		const videoIsPlaying = isVideoPlaying || hasPlayingVideoCheck;

		if (videoIsPlaying && isBgmDucked) {
			// Video is playing and BGM is ducked - don't restore, keep it ducked!
			console.log(
				"🔊 restoreBgmVolumeWithFade: Video playing and BGM ducked - NOT restoring, keeping ducked volume:",
				duckVolume
			);
			// Instead of returning, set volume to ducked level to ensure it's correct
			// BUT: Don't call setBgmVolume if muted (it will skip anyway, but be explicit)
			if (!isBgmMuted()) {
				setBgmVolume(duckVolume);
				audioStore.setBgmVolume(duckVolume);
			}
			// Don't clear previousBgmVolume - we might need it later
			return; // Don't restore if video is playing
		}
	} else if (hasPlayingVideoCheck) {
		// No store, but video is playing - don't restore
		console.log(
			"🔊 restoreBgmVolumeWithFade: Video element is playing - NOT restoring"
		);
		return;
	}

	const restoreVolume =
		targetVolume !== null ? targetVolume : previousBgmVolume || 0.5;

	// CRITICAL: Double-check audioStore state AND video elements before actually restoring
	// This prevents race conditions where state changes between the check above and the restore below
	const hasPlayingVideoDoubleCheck = hasPlayingVideo();

	if (audioStore) {
		const { isVideoPlaying, isBgmDucked, duckVolume } = audioStore;
		const videoIsPlayingCheck = isVideoPlaying || hasPlayingVideoDoubleCheck;

		if (videoIsPlayingCheck && isBgmDucked) {
			// State changed - video is now playing, don't restore!
			console.log(
				"🔊 restoreBgmVolumeWithFade: State changed - video now playing, NOT restoring, keeping ducked volume:",
				duckVolume
			);
			if (!isBgmMuted()) {
				setBgmVolume(duckVolume);
				audioStore.setBgmVolume(duckVolume);
			}
			return;
		}
	} else if (hasPlayingVideoDoubleCheck) {
		// No store, but video is playing - don't restore
		console.log(
			"🔊 restoreBgmVolumeWithFade: Video element is playing (double-check) - NOT restoring"
		);
		return;
	}

	// Sync with audioStore if available
	if (audioStore) {
		audioStore.restoreBgm();
		audioStore.setBgmVolume(restoreVolume);
	}

	fadeBgmVolume(restoreVolume, duration);
	previousBgmVolume = null;
}

// ============================================
// Global Mute/Unmute (for mute button)
// ============================================

/**
 * Toggle global mute state for all audio (BGM + video)
 * Handles complex volume restoration logic with ducking
 * @param {Object} options
 * @param {boolean} options.currentMuted - Current muted state
 * @param {boolean} options.disableBgm - Whether BGM should be disabled on this page
 * @param {Object} options.audioStore - Audio store instance for state access
 * @param {Object} options.userStore - User store instance for persistence
 * @returns {Promise<boolean>} - New muted state
 */
export async function toggleGlobalMute({
	currentMuted,
	disableBgm = false,
	audioStore = null,
	userStore = null,
} = {}) {
	const newMutedState = !currentMuted;

	try {
		if (newMutedState) {
			// ============================================
			// MUTING
			// ============================================
			if (!disableBgm) {
				// Save current BGM volume before muting
				const currentVolume = getBgmVolume();
				const { isVideoPlaying, isBgmDucked, duckVolume } = audioStore || {};

				// Track what volume we should restore to when unmuting
				if (isVideoPlaying && isBgmDucked) {
					// Video is playing - save ducked volume
					previousBgmVolume = duckVolume || 0.1;
				} else if (currentVolume > 0) {
					// Normal case - save current volume
					previousBgmVolume = currentVolume;
				}

				setBgmMuted(true);
			}

			// Mute all HTML5 audio/video elements
			const htmlBgm = getHtmlBgmElement();
			if (htmlBgm) {
				htmlBgm.muted = true;
			}

			document.querySelectorAll("audio").forEach((audio) => {
				audio.muted = true;
			});

			document.querySelectorAll("video").forEach((video) => {
				video.muted = true;
				video.volume = 0;
			});
		} else {
			// ============================================
			// UNMUTING
			// ============================================
			if (!disableBgm) {
				const system = getActiveBgmSystem();

				if (system) {
					// Set flag to prevent restoreBgmVolumeWithFade from interfering
					isUnmuting = true;

					// Determine correct volume to restore
					const { isVideoPlaying, duckVolume, getUnmuteVolume } =
						audioStore || {};

					// Check if there's actually a video playing
					const videoIsPlaying = isVideoPlaying || hasPlayingVideo();

					// Determine restore volume
					let volumeToRestore;
					if (videoIsPlaying) {
						// Video is playing - use ducked volume
						// Use store's duckVolume, or fallback to 0.1 (matches StoryVideo's default)
						volumeToRestore = duckVolume || 0.1;
					} else if (previousBgmVolume !== null) {
						// Use saved volume from when we muted
						volumeToRestore = previousBgmVolume;
					} else if (getUnmuteVolume) {
						// Fallback to store's getUnmuteVolume
						volumeToRestore = getUnmuteVolume();
					} else {
						// Final fallback - default full BGM volume
						volumeToRestore = 0.2; // Adjust this value (0.0 to 1.0)
					}

					// CRITICAL: Unmute FIRST, then set volume
					// setBgmVolume() skips if muted, so we must unmute first
					setBgmMuted(false);

					// Now set volume on audio element directly
					const bgmElement = getHtmlBgmElement();
					if (bgmElement) {
						bgmElement.volume = volumeToRestore;
					}

					// Set via audioManager and sync with store
					setBgmVolume(volumeToRestore);
					if (audioStore?.setBgmVolume) {
						audioStore.setBgmVolume(volumeToRestore);
					}

					// Resume BGM if not playing
					if (!isBgmPlaying()) {
						try {
							await resumeBgm();
							// Set volume again after resume
							setBgmVolume(volumeToRestore);
							if (audioStore?.setBgmVolume) {
								audioStore.setBgmVolume(volumeToRestore);
							}
						} catch (err) {
							console.warn("Failed to resume BGM on unmute:", err);
						}
					}

					// Final volume check with timeout
					setTimeout(() => {
						// CRITICAL: Re-check if video is playing before setting final volume
						// The useVideoAudio hook might have ducked the volume in the meantime
						const {
							isVideoPlaying: storeVideoPlaying,
							duckVolume: storeDuckVolume,
						} = audioStore || {};
						const videoStillPlaying = storeVideoPlaying || hasPlayingVideo();

						// If video is playing, don't force volume - let useVideoAudio handle it
						if (!videoStillPlaying) {
							const finalVolume = getBgmVolume();
							if (Math.abs(finalVolume - volumeToRestore) > 0.01) {
								setBgmVolume(volumeToRestore);
								if (audioStore?.setBgmVolume) {
									audioStore.setBgmVolume(volumeToRestore);
								}
							}
						} else {
							// Video is playing - ensure volume is ducked
							const correctDuckVolume = storeDuckVolume || 0.1;
							const finalVolume = getBgmVolume();
							if (Math.abs(finalVolume - correctDuckVolume) > 0.01) {
								setBgmVolume(correctDuckVolume);
								if (audioStore?.setBgmVolume) {
									audioStore.setBgmVolume(correctDuckVolume);
								}
							}
						}
						// Clear unmuting flag
						isUnmuting = false;
					}, 100);

					// Clear saved volume
					previousBgmVolume = null;
				} else {
					// No active Web Audio system - handle HTML5 BGM
					await resumeHtmlBgmIfExists();
				}
			} else {
				// BGM disabled but still unmute HTML5 BGM if it exists
				await resumeHtmlBgmIfExists();
			}

			// Unmute all HTML5 audio/video elements
			document.querySelectorAll("audio").forEach((audio) => {
				audio.muted = false;
			});

			document.querySelectorAll("video").forEach((video) => {
				video.muted = false;
				video.volume = 1.0;
			});
		}

		// Persist to localStorage
		try {
			localStorage.setItem("storyMuted", String(newMutedState));
			localStorage.setItem("connectAudio", newMutedState ? "without" : "with");
		} catch (e) {
			console.warn("Failed to save mute state to localStorage:", e);
		}

		// Update userStore
		if (userStore?.setStoryMuted) {
			userStore.setStoryMuted(newMutedState);
		}

		return newMutedState;
	} catch (error) {
		console.error("Error toggling global mute:", error);
		return currentMuted; // Return unchanged state on error
	}
}

// ============================================
// Combined Audio Unlock (for convenience)
// ============================================

/**
 * Unlock audio and optionally start BGM
 * Matches audioEngine behavior exactly for Safari iOS compatibility
 * Uses Web Audio directly (not HTML5) to ensure Safari iOS works correctly
 * @param {Object} options
 * @param {HTMLVideoElement} options.video - Video element to route
 * @param {string} options.bgmUrl - BGM URL to start
 * @param {number} options.bgmVolume - BGM volume
 * @returns {Promise<boolean>}
 */
export async function unlockAllAudio({
	video = null,
	bgmUrl = DEFAULT_BGM_URL,
	bgmVolume = 0.1,
} = {}) {
	// 1. Unlock AudioContext
	const audioUnlocked = await unlockAudioContext();

	// 2. Route video audio if provided
	if (video) {
		routeVideoAudio(video);
		video.muted = false;
	}

	// 3. Init + start BGM using Web Audio directly (matches audioEngine behavior)
	// CRITICAL: For Safari iOS, we must use Web Audio directly, not the unified startBgm
	// which might choose HTML5. This matches the original audioEngine implementation.
	const bgm = getWebBgmAudio();
	if (!bgm) {
		// BGM not initialized - initialize and play using Web Audio
		await ensureWebBgmStarted({ url: bgmUrl, volume: bgmVolume, muted: false });
		activeBgmSystem = "web";
	} else if (!isWebBgmPlaying()) {
		// BGM initialized but not playing - resume using Web Audio
		await resumeWebBgm();
		activeBgmSystem = "web";
	}

	return audioUnlocked || getAudioContext().state === "running";
}

// ============================================
// BGM Info & Debugging
// ============================================

/**
 * Get comprehensive BGM info
 * @returns {Object}
 */
export function getBgmInfo() {
	const system = getActiveBgmSystem();
	if (system === "web") {
		const audio = getWebBgmAudio();
		return {
			system: "web",
			url: getWebBgmUrl(),
			volume: audio?.volume || 0,
			muted: isWebBgmMuted(),
			isPlaying: isWebBgmPlaying(),
			position: getWebBgmPosition(),
		};
	} else if (system === "html5") {
		const info = getHtmlBgmInfo();
		return {
			system: "html5",
			url: info.src,
			volume: info.volume,
			muted: info.muted,
			isPlaying: info.isPlaying,
			position: info.currentTime,
		};
	}
	return {
		system: null,
		url: null,
		volume: 0,
		muted: false,
		isPlaying: false,
		position: 0,
	};
}

/**
 * Stop all BGM systems
 */
export function stopAllBgm() {
	pauseWebBgm();
	pauseHtmlBgm();
	activeBgmSystem = null;
	previousBgmVolume = null;
}

// ============================================
// Cleanup
// ============================================

/**
 * Cleanup all audio systems
 */
export function cleanup() {
	stopAllBgm();
	unrouteVideoAudio();
	cleanupWebBgm();
	cleanupHtmlBgm();
	activeBgmSystem = null;
	previousBgmVolume = null;
	videoSource = null;
	videoGain = null;
}

// ============================================
// Backward Compatibility Exports
// ============================================

// Expose audioManager to window for GlobalMuteButton to set isUnmuting flag
if (typeof window !== "undefined") {
	window.__audioManager = {
		get isUnmuting() {
			return isUnmuting;
		},
		set isUnmuting(value) {
			isUnmuting = value;
		},
	};
}

// Export Web Audio BGM getter for components that need direct access
export function getBgmAudio() {
	return getWebBgmAudio();
}

// Export HTML5 BGM element getter for components that need direct access
export function getBgmElement() {
	return getHtmlBgmElement();
}

// Note: getAudioContext() is already exported above
